import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { Globals } from '@theme/utils/globals';
import { EmitterService } from '@service/emitter/emitter.service';
import { ElectriciteService } from '@service/electricite/electricite.service';

interface HouseItem {
  id: number;
  uuid: string;
  nom: string;
  rentalsCount?: number;
  selected: boolean;
}

interface ContractRow {
  uuid: string;
  code: string;
  porte: string;
  libelle: string;
  houseNom: string;
  tenantNom: string;
  lastIndex: number;
  lastDate: string;
  debut: number;
  fin: number;
  conso: number;
  excluded: boolean;
}

interface PreviewLine {
  ligne: number;
  contract: string;
  porte: string;
  debut: number;
  fin: number;
  date: string;
  statut: string;
  message: string;
  conso: number;
  montant: number;
  ancienMontant: number;
  loyerAvant: number;
  loyerApres: number;
  locataire: string;
  prix: number;
  rentUuid: string;
  editable: boolean;
  editing?: boolean;
  moisRent?: string;
  dateRent?: string;
}

@Component({
  selector: 'app-electricite-wizard',
  templateUrl: './electricite-wizard.component.html',
  styleUrls: ['./electricite-wizard.component.scss']
})
export class ElectriciteWizardComponent implements OnInit {

  currentStep: number = 1;
  title: string = 'Import consommation d\'électricité';

  periode: string = '';
  datePrelevement: string = '';
  houseSearch: string = '';
  houses: HouseItem[] = [];
  loadingHouses: boolean = false;

  activeTab: 'manual' | 'upload' = 'manual';
  contractRows: ContractRow[] = [];
  loadingContracts: boolean = false;
  selectedFile: File = null;
  uploadLoading: boolean = false;

  previewData: PreviewLine[] = [];
  previewLoading: boolean = false;
  submitting: boolean = false;

  totalNouveau: number = 0;
  totalMiseAJour: number = 0;
  totalErreur: number = 0;
  montantTotal: number = 0;

  global = { country: Globals.country, device: Globals.device };

  constructor(
    public modal: NgbActiveModal,
    public toastr: ToastrService,
    private emitter: EmitterService,
    private electriciteService: ElectriciteService
  ) {}

  ngOnInit(): void {
    const today = new Date();
    this.datePrelevement = today.toISOString().substring(0, 10);
    this.periode = today.toISOString().substring(0, 7) + '-01';
    this.loadHouses();
  }

  loadHouses(): void {
    this.loadingHouses = true;
    this.electriciteService.getHousesForWizard().subscribe(
      (res: any) => {
        this.loadingHouses = false;
        const list = res?.data || [];
        this.houses = list.map((h: any) => ({
          id: h.id,
          uuid: h.uuid,
          nom: h.nom,
          rentalsCount: h.rentalsCount || 0,
          selected: false
        }));
      },
      () => {
        this.loadingHouses = false;
        this.toastr.error('Impossible de charger les immeubles', 'Erreur');
      }
    );
  }

  get filteredHouses(): HouseItem[] {
    if (!this.houseSearch) { return this.houses; }
    const q = this.houseSearch.toLowerCase();
    return this.houses.filter(h => h.nom && h.nom.toLowerCase().includes(q));
  }

  get selectedHouses(): HouseItem[] {
    return this.houses.filter(h => h.selected);
  }

  get allFilteredSelected(): boolean {
    const list = this.filteredHouses;
    return list.length > 0 && list.every(h => h.selected);
  }

  toggleHouse(h: HouseItem): void { h.selected = !h.selected; }

  toggleAll(): void {
    const list = this.filteredHouses;
    const checked = !this.allFilteredSelected;
    list.forEach(h => h.selected = checked);
  }

  get step1Valid(): boolean {
    return !!this.periode && !!this.datePrelevement && this.selectedHouses.length > 0;
  }

  goToStep2(): void {
    if (!this.step1Valid) {
      this.toastr.warning('Sélectionnez une période, une date et au moins un immeuble', 'Attention');
      return;
    }
    this.loadContractsForSelected();
    this.currentStep = 2;
  }

  loadContractsForSelected(): void {
    this.loadingContracts = true;
    const ids = this.selectedHouses.map(h => h.id);
    this.electriciteService.getContractsByHouses(ids, this.periode).subscribe(
      (res: any) => {
        this.loadingContracts = false;
        const data = res?.data || [];
        this.contractRows = data.map((c: any) => ({
          uuid: c.uuid,
          code: c.code,
          porte: c.porte,
          libelle: c.libelle,
          houseNom: c.house ? c.house.nom : '',
          tenantNom: c.tenant ? c.tenant.nom : '',
          lastIndex: c.lastIndex || 0,
          lastDate: c.lastDate,
          debut: c.lastIndex || 0,
          fin: 0,
          conso: 0,
          excluded: false
        }));
      },
      () => {
        this.loadingContracts = false;
        this.toastr.error('Impossible de charger les contrats', 'Erreur');
      }
    );
  }

  onFinChange(row: ContractRow): void {
    const debut = parseFloat(String(row.debut)) || 0;
    const fin = parseFloat(String(row.fin)) || 0;
    row.conso = fin > debut ? parseFloat((fin - debut).toFixed(1)) : 0;
  }

  onFileSelected(event: any): void {
    const f = event?.target?.files?.[0];
    if (f) { this.selectedFile = f; }
  }

  clearFile(): void { this.selectedFile = null; }

  downloadTemplate(): void {
    const ids = this.selectedHouses.map(h => h.id);
    if (ids.length === 0) {
      this.toastr.warning('Retournez à l\'étape 1 pour sélectionner au moins un immeuble', 'Attention');
      return;
    }
    this.electriciteService.downloadTargetedTemplate(ids, this.periode).subscribe(
      () => { this.toastr.success('Template téléchargé', 'Succès'); },
      (err: any) => {
        // Le backend peut renvoyer un JSON d'erreur sous forme de Blob
        if (err?.error instanceof Blob) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const parsed = JSON.parse(reader.result as string);
              this.toastr.error(parsed?.message || 'Erreur lors du téléchargement', 'Erreur');
            } catch {
              this.toastr.error('Erreur lors du téléchargement du template', 'Erreur');
            }
          };
          reader.readAsText(err.error);
        } else {
          this.toastr.error(err?.error?.message || 'Erreur lors du téléchargement du template', 'Erreur');
        }
      }
    );
  }

  goToStep3(): void {
    if (this.activeTab === 'manual') {
      const active = this.contractRows.filter(r => !r.excluded && r.fin > 0);
      if (active.length === 0) {
        this.toastr.warning('Renseignez au moins un index de fin', 'Attention');
        return;
      }
      const options = active.map(r => ({
        contract: r.uuid,
        porte: r.porte,
        debut: r.debut,
        fin: r.fin,
        conso: r.conso,
        date: this.datePrelevement
      }));
      this.runPreview(options);
    } else {
      if (!this.selectedFile) {
        this.toastr.warning('Veuillez sélectionner un fichier Excel', 'Attention');
        return;
      }
      this.uploadLoading = true;
      this.electriciteService.importExcelFile(this.selectedFile, this.periode).subscribe(
        (res: any) => {
          this.uploadLoading = false;
          if (res?.status === 'success') {
            const options = (res?.data?.options || []).map((o: any) => ({
              ...o,
              date: o.date || this.datePrelevement
            }));
            this.runPreview(options);
          } else {
            this.toastr.error(res?.message || 'Erreur lors de la lecture du fichier', 'Erreur');
          }
        },
        (err) => {
          this.uploadLoading = false;
          this.toastr.error(err?.error?.message || 'Erreur lors de la lecture du fichier', 'Erreur');
        }
      );
    }
  }

  runPreview(options: any[]): void {
    this.previewLoading = true;
    const payload = { options: options, periode: this.periode, date: this.datePrelevement };
    this.electriciteService.previewImport(payload).subscribe(
      (res: any) => {
        this.previewLoading = false;
        if (res?.status === 'success') {
          this.previewData = (res?.data || []).map((p: any) => ({ ...p, editing: false }));
          this.calculateStats();
          this.currentStep = 3;
        } else {
          this.toastr.error(res?.message || 'Erreur lors du preview', 'Erreur');
        }
      },
      () => {
        this.previewLoading = false;
        this.toastr.error('Erreur lors de la génération du preview', 'Erreur');
      }
    );
  }

  calculateStats(): void {
    this.totalNouveau = this.previewData.filter(p => p.statut === 'NOUVEAU').length;
    this.totalMiseAJour = this.previewData.filter(p => p.statut === 'MISE_A_JOUR').length;
    this.totalErreur = this.previewData.filter(p => p.statut === 'ERREUR').length;
    this.montantTotal = this.previewData
      .filter(p => p.statut !== 'ERREUR')
      .reduce((sum, p) => sum + (p.montant || 0), 0);
  }

  getStatusBadgeClass(statut: string): string {
    switch (statut) {
      case 'NOUVEAU': return 'badge-success';
      case 'MISE_A_JOUR': return 'badge-warning';
      case 'ERREUR': return 'badge-danger';
      default: return 'badge-secondary';
    }
  }

  editLine(line: PreviewLine): void { line.editing = true; }

  cancelEdit(line: PreviewLine): void { line.editing = false; }

  saveLine(line: PreviewLine): void {
    line.conso = line.fin - line.debut;
    if (line.prix) {
      line.montant = Math.round(line.conso * line.prix);
      line.loyerApres = line.loyerAvant - line.ancienMontant + line.montant;
    }
    line.editing = false;
    this.calculateStats();
  }

  removeLine(index: number): void {
    this.previewData.splice(index, 1);
    this.calculateStats();
  }

  onValidate(): void {
    const valid = this.previewData.filter(p => p.statut !== 'ERREUR');
    if (valid.length === 0) {
      this.toastr.error('Aucune ligne valide à importer', 'Erreur');
      return;
    }
    Swal.fire({
      title: 'Confirmation',
      text: `Voulez-vous enregistrer ${valid.length} ligne(s) ?`,
      icon: 'question',
      showCancelButton: true,
      showCloseButton: true,
      confirmButtonText: 'Valider <i class="fas fa-check"></i>',
      cancelButtonText: 'Annuler <i class="feather icon-x-circle"></i>',
      confirmButtonColor: '#1bc943',
      reverseButtons: true
    }).then((result) => { if (!result.dismiss) { this.submitValidation(); } });
  }

  submitValidation(): void {
    this.submitting = true;
    const options = this.previewData
      .filter(p => p.statut !== 'ERREUR')
      .map(p => ({
        contract: p.contract,
        porte: p.porte,
        debut: p.debut,
        fin: p.fin,
        conso: p.conso,
        date: p.date
      }));
    const payload = { options: options, periode: this.periode, date: this.datePrelevement };
    this.electriciteService.validatePreview(payload).subscribe(
      (res: any) => {
        this.submitting = false;
        if (res?.status === 'success') {
          this.toastr.success('Import électricité enregistré avec succès', 'Succès');
          this.emitter.emit({ action: 'ELECTRICITE_IMPORT', payload: res?.data });
          this.modal.close('ferme');
        }
      },
      () => {
        this.submitting = false;
        this.toastr.error('Erreur lors de l\'enregistrement', 'Erreur');
      }
    );
  }

  back(): void {
    if (this.currentStep > 1) { this.currentStep--; }
  }

  onClose(): void { this.modal.close('ferme'); }
}
